import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Loader2, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { DocumentUploader } from '@/components/Uploaders';
import { cn } from '@/lib/utils';

const CITY_COORDS = {
  Jakarta: { prov: 'DKI Jakarta', lat: -6.2088, lng: 106.8456 },
  Bandung: { prov: 'Jawa Barat', lat: -6.9175, lng: 107.6191 },
  Surabaya: { prov: 'Jawa Timur', lat: -7.2575, lng: 112.7521 },
  Yogyakarta: { prov: 'DI Yogyakarta', lat: -7.7956, lng: 110.3695 },
  Denpasar: { prov: 'Bali', lat: -8.6705, lng: 115.2126 },
  Bali: { prov: 'Bali', lat: -8.3405, lng: 115.092 },
  Medan: { prov: 'Sumatera Utara', lat: 3.5952, lng: 98.6722 },
  Balikpapan: { prov: 'Kalimantan Timur', lat: -1.2379, lng: 116.8529 },
  Manado: { prov: 'Sulawesi Utara', lat: 1.4748, lng: 124.8421 },
  Palembang: { prov: 'Sumatera Selatan', lat: -2.9909, lng: 104.7566 },
  Semarang: { prov: 'Jawa Tengah', lat: -6.9667, lng: 110.4167 },
  Makassar: { prov: 'Sulawesi Selatan', lat: -5.1477, lng: 119.4327 },
};

const COUNTRY_COORDS = {
  'Jepang': { city: 'Tokyo', lat: 35.6762, lng: 139.6503 },
  'Korea Selatan': { city: 'Seoul', lat: 37.5665, lng: 126.978 },
  'Singapura': { city: 'Singapore', lat: 1.3521, lng: 103.8198 },
  'Malaysia': { city: 'Kuala Lumpur', lat: 3.139, lng: 101.6869 },
  'Tiongkok': { city: 'Beijing', lat: 39.9042, lng: 116.4074 },
  'Australia': { city: 'Sydney', lat: -33.8688, lng: 151.2093 },
  'Amerika Serikat': { city: 'Washington DC', lat: 38.9072, lng: -77.0369 },
  'Belanda': { city: 'Den Haag', lat: 52.0705, lng: 4.3007 },
  'Jerman': { city: 'Berlin', lat: 52.52, lng: 13.405 },
  'Inggris': { city: 'London', lat: 51.5074, lng: -0.1278 },
  'Arab Saudi': { city: 'Riyadh', lat: 24.7136, lng: 46.6753 },
  'Uni Emirat Arab': { city: 'Dubai', lat: 25.2048, lng: 55.2708 },
  'Turki': { city: 'Istanbul', lat: 41.0082, lng: 28.9784 },
  'Mesir': { city: 'Cairo', lat: 30.0444, lng: 31.2357 },
  'Korea Utara': { city: 'Pyongyang', lat: 39.0392, lng: 125.7625 },
  'Vietnam': { city: 'Hanoi', lat: 21.0285, lng: 105.8542 },
  'Thailand': { city: 'Bangkok', lat: 13.7563, lng: 100.5018 },
  'Filipina': { city: 'Manila', lat: 14.5995, lng: 120.9842 },
  'India': { city: 'New Delhi', lat: 28.6139, lng: 77.209 },
};

const CURRENCIES = ['IDR', 'USD', 'EUR', 'SGD', 'JPY', 'AUD', 'MYR', 'CNY', 'GBP', 'SAR', 'AED'];

export default function SPPDFormPage() {
  const { id } = useParams();
  const editing = !!id;
  const navigate = useNavigate();
  const [pejabats, setPejabats] = useState([]);
  const [opds, setOpds] = useState([]);
  const [loading, setLoading] = useState(editing);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    jenis_perjalanan: 'Dalam Negeri',
    pejabat_id: '', pejabat_nama: '', jabatan: '', opd_id: '', opd_nama: '',
    tujuan_kota: '', tujuan_provinsi: '', lat: null, lng: null,
    negara_tujuan: '', kota_luar_negeri: '', instansi_tujuan: '', nomor_paspor: '',
    mata_uang_biaya: 'IDR', keterangan_tambahan: '',
    keperluan: '', tanggal_berangkat: '', tanggal_kembali: '',
    transportasi: 'Pesawat', sumber_dana: 'APBD Kota Makassar TA 2026',
    biaya_estimasi: 0, pendamping_nama: '', catatan: '',
    dokumen: [],
  });

  useEffect(() => {
    Promise.all([api.get('/pejabat?is_active=true'), api.get('/opd')]).then(([p, o]) => {
      setPejabats(p.data);
      setOpds(o.data);
    });
    if (editing) {
      api.get(`/sppd/${id}`).then((r) => {
        const s = r.data;
        setForm({
          jenis_perjalanan: s.jenis_perjalanan || 'Dalam Negeri',
          pejabat_id: s.pejabat_id || '', pejabat_nama: s.pejabat_nama || '', jabatan: s.jabatan || '',
          opd_id: s.opd_id || '', opd_nama: s.opd_nama || '',
          tujuan_kota: s.tujuan_kota || '', tujuan_provinsi: s.tujuan_provinsi || '',
          lat: s.lat, lng: s.lng,
          negara_tujuan: s.negara_tujuan || '', kota_luar_negeri: s.kota_luar_negeri || '',
          instansi_tujuan: s.instansi_tujuan || '', nomor_paspor: s.nomor_paspor || '',
          mata_uang_biaya: s.mata_uang_biaya || 'IDR', keterangan_tambahan: s.keterangan_tambahan || '',
          keperluan: s.keperluan || '',
          tanggal_berangkat: (s.tanggal_berangkat || '').slice(0, 10),
          tanggal_kembali: (s.tanggal_kembali || '').slice(0, 10),
          transportasi: s.transportasi || 'Pesawat',
          sumber_dana: s.sumber_dana || '',
          biaya_estimasi: s.biaya_estimasi || 0,
          pendamping_nama: (s.pendamping_nama || []).join(', '),
          catatan: s.catatan || '',
          dokumen: s.dokumen || [],
        });
      }).finally(() => setLoading(false));
    }
  }, [editing, id]);

  const onPejabatChange = (pid) => {
    const p = pejabats.find((x) => x.id === pid);
    setForm({ ...form, pejabat_id: pid, pejabat_nama: p?.nama || '', jabatan: p?.jabatan || '', opd_id: p?.opd_id || '', opd_nama: p?.opd_nama || '' });
  };

  const onKotaChange = (val) => {
    const c = CITY_COORDS[val];
    setForm({ ...form, tujuan_kota: val, tujuan_provinsi: c?.prov || '', lat: c?.lat ?? null, lng: c?.lng ?? null });
  };

  const onNegaraChange = (val) => {
    const c = COUNTRY_COORDS[val];
    setForm({ ...form, negara_tujuan: val, kota_luar_negeri: c?.city || form.kota_luar_negeri, lat: c?.lat ?? null, lng: c?.lng ?? null });
  };

  const isLN = form.jenis_perjalanan === 'Luar Negeri';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.pejabat_id || !form.keperluan || !form.tanggal_berangkat || !form.tanggal_kembali) {
      toast.error('Mohon lengkapi field wajib');
      return;
    }
    if (isLN) {
      if (!form.negara_tujuan || !form.kota_luar_negeri) {
        toast.error('Negara dan Kota tujuan wajib diisi untuk SPPD Luar Negeri');
        return;
      }
    } else if (!form.tujuan_kota) {
      toast.error('Kota tujuan wajib diisi');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        biaya_estimasi: Number(form.biaya_estimasi) || 0,
        tanggal_berangkat: new Date(form.tanggal_berangkat).toISOString(),
        tanggal_kembali: new Date(form.tanggal_kembali).toISOString(),
        pendamping_nama: form.pendamping_nama.split(',').map((s) => s.trim()).filter(Boolean),
      };
      if (editing) {
        await api.patch(`/sppd/${id}`, payload);
        toast.success('SPPD berhasil diperbarui');
      } else {
        await api.post('/sppd', payload);
        toast.success('SPPD berhasil diregistrasi dan menunggu approval Kabag');
      }
      navigate('/sppd');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menyimpan SPPD');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="py-16 flex justify-center"><div className="animate-spin w-6 h-6 border-2 border-red-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="space-y-6 max-w-5xl">
      <PageHeader
        title={editing ? 'Edit SPPD' : 'Registrasi SPPD'}
        subtitle="Lengkapi data perjalanan dinas (Dalam Negeri / Luar Negeri). SPPD akan menunggu approval Kabag Prokopim."
        breadcrumb={[{ label: 'Beranda', to: '/dashboard' }, { label: 'SPPD', to: '/sppd' }, { label: editing ? 'Edit' : 'Registrasi' }]}
        action={<Button variant="outline" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4 mr-2" />Kembali</Button>}
      />

      <form onSubmit={submit} className="space-y-5">
        {/* Jenis Perjalanan Toggle */}
        <Card className="border-slate-200 shadow-premium">
          <CardContent className="p-4">
            <Label className="mb-3 block">Jenis Perjalanan Dinas</Label>
            <div className="grid grid-cols-2 gap-3">
              {['Dalam Negeri', 'Luar Negeri'].map((jenis) => {
                const Icon = jenis === 'Luar Negeri' ? Globe : MapPin;
                const active = form.jenis_perjalanan === jenis;
                return (
                  <button
                    key={jenis}
                    type="button"
                    data-testid={`jenis-${jenis === 'Luar Negeri' ? 'ln' : 'dn'}`}
                    onClick={() => setForm({ ...form, jenis_perjalanan: jenis })}
                    className={cn(
                      'p-4 rounded-xl border-2 text-left transition-all',
                      active
                        ? 'border-[#DC2626] bg-red-50/40 shadow-premium'
                        : 'border-slate-200 bg-white hover:border-slate-300'
                    )}
                  >
                    <Icon className={cn('w-6 h-6 mb-2', active ? 'text-[#DC2626]' : 'text-slate-400')} />
                    <div className={cn('font-bold', active && 'text-[#DC2626]')}>{jenis}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {jenis === 'Dalam Negeri' ? 'Perjalanan dalam wilayah Indonesia' : 'Perjalanan ke luar negeri (paspor diperlukan)'}
                    </div>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Data Pejabat</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-1.5">
              <Label>Pejabat yang Dinas <span className="text-red-600">*</span></Label>
              <Select value={form.pejabat_id} onValueChange={onPejabatChange}>
                <SelectTrigger data-testid="sppd-pejabat"><SelectValue placeholder="Pilih pejabat" /></SelectTrigger>
                <SelectContent>
                  {pejabats.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama} — {p.jabatan}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Jabatan</Label><Input value={form.jabatan} readOnly className="bg-slate-50" /></div>
            <div className="space-y-1.5"><Label>OPD</Label><Input value={form.opd_nama} readOnly className="bg-slate-50" /></div>
          </CardContent>
        </Card>

        {!isLN ? (
          <Card className="border-slate-200 shadow-premium">
            <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><MapPin className="w-4 h-4 text-[#DC2626]" />Tujuan Dalam Negeri</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Kota Tujuan <span className="text-red-600">*</span></Label>
                <Select value={form.tujuan_kota} onValueChange={onKotaChange}>
                  <SelectTrigger data-testid="sppd-tujuan"><SelectValue placeholder="Pilih kota" /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(CITY_COORDS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Provinsi</Label><Input value={form.tujuan_provinsi} readOnly className="bg-slate-50" /></div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-slate-200 shadow-premium" data-testid="sppd-ln-section">
            <CardHeader><CardTitle className="text-base font-bold flex items-center gap-2"><Globe className="w-4 h-4 text-[#DC2626]" />Tujuan Luar Negeri</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Negara Tujuan <span className="text-red-600">*</span></Label>
                <Select value={form.negara_tujuan} onValueChange={onNegaraChange}>
                  <SelectTrigger data-testid="sppd-negara"><SelectValue placeholder="Pilih negara" /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(COUNTRY_COORDS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Kota Tujuan <span className="text-red-600">*</span></Label>
                <Input value={form.kota_luar_negeri} onChange={(e) => setForm({ ...form, kota_luar_negeri: e.target.value })} placeholder="Tokyo / Seoul / Singapore" />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label>Instansi Tujuan</Label>
                <Input value={form.instansi_tujuan} onChange={(e) => setForm({ ...form, instansi_tujuan: e.target.value })} placeholder="Mis. Kementerian Luar Negeri Jepang" />
              </div>
              <div className="space-y-1.5">
                <Label>Nomor Paspor</Label>
                <Input data-testid="sppd-paspor" value={form.nomor_paspor} onChange={(e) => setForm({ ...form, nomor_paspor: e.target.value })} placeholder="B1234567" />
              </div>
              <div className="space-y-1.5">
                <Label>Mata Uang Biaya</Label>
                <Select value={form.mata_uang_biaya} onValueChange={(v) => setForm({ ...form, mata_uang_biaya: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label>Keterangan Tambahan</Label>
                <Textarea rows={2} value={form.keterangan_tambahan} onChange={(e) => setForm({ ...form, keterangan_tambahan: e.target.value })} placeholder="Ijin Kemenlu, visa, dll." />
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Periode & Keperluan</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2 space-y-1.5">
              <Label>Keperluan <span className="text-red-600">*</span></Label>
              <Textarea rows={2} value={form.keperluan} onChange={(e) => setForm({ ...form, keperluan: e.target.value })} placeholder="Tujuan / agenda perjalanan dinas" />
            </div>
            <div className="space-y-1.5">
              <Label>Tanggal Berangkat <span className="text-red-600">*</span></Label>
              <Input type="date" data-testid="sppd-berangkat" value={form.tanggal_berangkat} onChange={(e) => setForm({ ...form, tanggal_berangkat: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Tanggal Kembali <span className="text-red-600">*</span></Label>
              <Input type="date" data-testid="sppd-kembali" value={form.tanggal_kembali} onChange={(e) => setForm({ ...form, tanggal_kembali: e.target.value })} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Logistik & Biaya</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Transportasi</Label>
              <Select value={form.transportasi} onValueChange={(v) => setForm({ ...form, transportasi: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pesawat">Pesawat</SelectItem>
                  <SelectItem value="Kereta">Kereta</SelectItem>
                  <SelectItem value="Kapal">Kapal</SelectItem>
                  <SelectItem value="Mobil Dinas">Mobil Dinas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Sumber Dana</Label>
              <Input value={form.sumber_dana} onChange={(e) => setForm({ ...form, sumber_dana: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Biaya Estimasi {isLN && `(${form.mata_uang_biaya})`}</Label>
              <Input type="number" data-testid="sppd-biaya" value={form.biaya_estimasi} onChange={(e) => setForm({ ...form, biaya_estimasi: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Pendamping (pisahkan koma)</Label>
              <Input value={form.pendamping_nama} onChange={(e) => setForm({ ...form, pendamping_nama: e.target.value })} placeholder="Ajudan, Protokol, Penerjemah" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label>Catatan</Label>
              <Textarea rows={2} value={form.catatan} onChange={(e) => setForm({ ...form, catatan: e.target.value })} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-premium">
          <CardHeader><CardTitle className="text-base font-bold">Dokumen Pendukung</CardTitle></CardHeader>
          <CardContent>
            <DocumentUploader
              items={form.dokumen}
              onChange={(items) => setForm({ ...form, dokumen: items })}
              endpoint="/upload/dokumen-sppd"
              kindOptions={[
                { value: 'Surat Tugas', label: 'Surat Tugas' },
                { value: 'SPPD', label: 'SPPD' },
                { value: 'Tiket', label: 'Tiket' },
                { value: 'Boarding Pass', label: 'Boarding Pass' },
                { value: 'Laporan', label: 'Laporan Hasil Perjalanan' },
                { value: 'Lampiran', label: 'Lampiran Lain' },
              ]}
            />
          </CardContent>
        </Card>

        <div className="sticky bottom-4">
          <Card className="border-slate-200 shadow-elevated">
            <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-slate-500 hidden sm:block">Field bertanda <span className="text-red-600 font-semibold">*</span> wajib diisi.</div>
              <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                <Button type="button" variant="outline" onClick={() => navigate(-1)}>Batal</Button>
                <Button type="submit" disabled={submitting} data-testid="sppd-form-submit" className="bg-[#DC2626] hover:bg-[#B91C1C]">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Menyimpan…</> : <><Save className="w-4 h-4 mr-2" />{editing ? 'Simpan' : 'Registrasi'}</>}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </form>
    </div>
  );
}
